import { useEffect } from "react";
import * as z from "zod";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import {
  confirmResetPassword,
  ConfirmResetPasswordInput,
  signIn,
  SignInInput,
} from "@aws-amplify/auth";

import { useFormWithSchema } from "hooks/useFormWithSchema";
import AuthLayout from "component/Layout/AuthLayout";
import PasswordTextField from "component/Form/PasswordTextField";
import { APP_ROUTES } from "routes/appRoutes";
import { useAppNavigate } from "routes/useAppNavigate";
import { passwordSchema } from "schemas/userSchema";
import { notify } from "lib/notify";
import PlaceholderLogo from "./PlaceholderLogo";
import { handleError } from "utils/error/handleError";

const schema = z
  .object({
    code: z
      .string()
      .regex(/^[a-zA-Z0-9]+$/, { message: "半角英数字のみ入力できます" }),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "パスワードが一致しません",
    path: ["confirmPassword"],
  });

type VerifyFormInput = z.infer<typeof schema>;

const PasswordResetVerifyPage = () => {
  const { publicNavigate, authenticatedNavigate } = useAppNavigate();
  const location = useLocation();

  const onSubmit = async (data: VerifyFormInput) => {
    try {
      const state = location.state as { loginId?: string };
      if (!state || !state.loginId) {
        publicNavigate.login();
        notify("ログインIDが取得できません", "error");
        return;
      }

      const resetParams: ConfirmResetPasswordInput = {
        username: state.loginId,
        confirmationCode: data.code,
        newPassword: data.password,
      };
      await confirmResetPassword(resetParams);

      const signInParams: SignInInput = {
        username: state.loginId,
        password: data.password,
      };
      const res = await signIn(signInParams);
      if (
        res.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        // 仮パスワードだった場合はパスワード登録画面へ遷移
        publicNavigate.passwordSet({
          state: { signInStep: "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" },
        });
        return;
      }

      authenticatedNavigate.root();
      notify("パスワードを設定しました");
    } catch (err) {
      let overrideMessage: string =
        "エラーが発生しています。再度お試しください。";
      if (err instanceof Error && err.name === "CodeMismatchException") {
        overrideMessage =
          "不正な検証コードです。メールボックスを確認してください。";
      }
      handleError(err, { overrideMessage });
    }
  };

  const {
    formState: { isSubmitting, errors },
    register,
    onSubmitHandler,
  } = useFormWithSchema(schema, onSubmit);

  // 検証コードが取得できない場合はログイン画面へ遷移
  useEffect(() => {
    const state = location.state;
    if (!state || !state.loginId) {
      publicNavigate.login();
      notify("ログインIDが取得できません", "error");
    }
  }, [location.state, publicNavigate]);

  return (
    <AuthLayout>
      <Box
        component={"form"}
        onSubmit={onSubmitHandler}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Stack
          spacing={4}
          sx={{
            width: "100%",
          }}
        >
          <Stack spacing={2}>
            <Box display={"flex"} justifyContent={"center"}>
              <PlaceholderLogo />
            </Box>
            <Typography variant="h5">検証コード確認</Typography>
            <Typography variant="body2" color="text.secondary">
              登録されているメールアドレス宛に検証コードを送信しました。メールボックスを確認してください。
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <TextField
              label="検証コード"
              {...register("code")}
              error={!!errors.code}
              helperText={errors.code?.message}
              fullWidth
              name="code"
              type="text"
              disabled={isSubmitting}
              autoComplete="code"
            />
            <Box>
              <PasswordTextField
                label="新しいパスワード"
                {...register("password")}
                error={!!errors.password}
                helperText={errors.password?.message}
                fullWidth
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              <Typography
                sx={{ mt: 1 }}
                variant="passwordHint"
                color={"text.secondary"}
              >
                {
                  "パスワードは8文字以上20文字以内で、\n大文字・小文字・数字を含む英数字と、記号「-」「_」「.」が使えます。"
                }
              </Typography>
            </Box>
            <PasswordTextField
              label="新しいパスワード（確認用）"
              {...register("confirmPassword")}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              fullWidth
              disabled={isSubmitting}
              autoComplete="new-confirm-password"
            />
          </Stack>
          <Stack spacing={2}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              startIcon={
                isSubmitting && <CircularProgress size={20} color="inherit" />
              }
            >
              登録
            </Button>
            <Box textAlign={"center"}>
              <MuiLink
                sx={{
                  display: "inline-block",
                  width: "fit-content",
                }}
                component={RouterLink}
                to={APP_ROUTES.public.login.path}
              >
                <Typography variant="body2">ログイン画面に戻る</Typography>
              </MuiLink>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default PasswordResetVerifyPage;
